import React from 'react';
import {
  Github,
  Gem,
  Target,
  Brain,
  FolderTree,
  Network,
  Radar,
  ClipboardCheck,
  Tag,
  GitBranch,
  Download,
  FileSearch,
  ArrowRight,
} from 'lucide-react';
import usePersona from '../../hooks/usePersona';

/**
 * Persona-specific empty state messaging per tab.
 *
 * Structure: PERSONA_EMPTY_STATES[personaId][tabId] = {
 *   title, description, actionLabel, secondaryLabel
 * }
 *
 * The component merges these with the icon / callbacks passed as props,
 * so each tab controls its own icon and action handlers while the
 * persona config provides the copy.
 */
const PERSONA_EMPTY_STATES = {
  professional: {
    value: {
      title: 'Discover what your code is worth',
      description:
        'Import your repositories to get AI-powered value assessments, skill-backed evidence, and a portfolio story you can share with recruiters.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    skills: {
      title: 'Map your skill DNA',
      description:
        'Run analysis on your repositories to surface verified skills, proficiency levels, and growth trends that no resume can capture.',
      actionLabel: 'Import Repos',
      secondaryLabel: 'Run Analysis',
    },
    import: {
      title: 'Connect your GitHub to get started',
      description:
        'Link your repositories and let AI analyze your code patterns, languages, and contributions to build a verified skill profile.',
      actionLabel: 'Connect GitHub',
      secondaryLabel: null,
    },
    discovery: {
      title: 'Uncover hidden patterns',
      description:
        'Import and analyze your code to discover skill clusters, technology adoption trends, and career trajectory insights.',
      actionLabel: 'Import Repos',
      secondaryLabel: 'Run Analysis',
    },
    collections: {
      title: 'Organize your portfolio story',
      description:
        'Group repositories into strategic collections to highlight different facets of your experience.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    graph: {
      title: 'Visualize your collaboration network',
      description:
        'Import repositories to see how your projects relate, which skills bridge them, and where collaboration patterns emerge.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    innovation: {
      title: 'Track your technology adoption',
      description:
        'Analyze your repositories to see which technologies you adopted early, where you lead, and what is emerging in your stack.',
      actionLabel: 'Run Analysis',
      secondaryLabel: 'Import Repos',
    },
    assessment: {
      title: 'Assess your project maturity',
      description:
        'Import repositories to get automated quality assessments, maintainability scores, and improvement recommendations.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    classification: {
      title: 'Classify your project types',
      description:
        'Run analysis to automatically categorize your repositories by domain, architecture style, and strategic purpose.',
      actionLabel: 'Import Repos',
      secondaryLabel: 'Run Analysis',
    },
    genealogy: {
      title: 'Trace your project evolution',
      description:
        'Import repositories to see how your projects forked, evolved, and influenced each other over time.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    export: {
      title: 'Export your verified portfolio',
      description:
        'Import and analyze repositories first, then generate polished CVs, skill reports, and shareable portfolio packages.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
  },

  founder: {
    value: {
      title: 'Tell your technical story',
      description:
        'Import your startup\'s repos to generate investor-ready reports showcasing technology maturity, team capability, and defensibility.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    skills: {
      title: 'Showcase team capability',
      description:
        'Analyze your team\'s repositories to produce skill coverage maps that prove engineering depth to investors and partners.',
      actionLabel: 'Import Repos',
      secondaryLabel: 'Run Analysis',
    },
    import: {
      title: 'Connect your startup repos',
      description:
        'Link your organization\'s GitHub to automatically sync repositories and start building your technical due diligence package.',
      actionLabel: 'Connect GitHub',
      secondaryLabel: null,
    },
    collections: {
      title: 'Organize your product lines',
      description:
        'Group repositories by product, team, or funding round to create focused narratives for different stakeholders.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    discovery: {
      title: 'Discover your competitive edge',
      description:
        'Run analysis to identify unique technology patterns, innovation velocity, and moat indicators across your codebase.',
      actionLabel: 'Import Repos',
      secondaryLabel: 'Run Analysis',
    },
    graph: {
      title: 'Map your technical architecture',
      description:
        'Import repositories to visualize service dependencies, team ownership boundaries, and integration complexity.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    innovation: {
      title: 'Prove your innovation velocity',
      description:
        'Analyze repositories to quantify technology adoption speed, experimentation rate, and forward-looking stack choices.',
      actionLabel: 'Run Analysis',
      secondaryLabel: 'Import Repos',
    },
    assessment: {
      title: 'Assess production readiness',
      description:
        'Import repositories to evaluate code quality, test coverage, and operational maturity across your product.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    classification: {
      title: 'Classify your tech portfolio',
      description:
        'Run analysis to categorize repositories by strategic value -- core product, infrastructure, experiments, and tooling.',
      actionLabel: 'Import Repos',
      secondaryLabel: 'Run Analysis',
    },
    genealogy: {
      title: 'Show your technical evolution',
      description:
        'Import repositories to trace how your architecture evolved from MVP to scale, telling a compelling growth story.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    export: {
      title: 'Generate investor-ready reports',
      description:
        'Analyze your repositories first, then export due diligence packages, team capability summaries, and technology assessments.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
  },

  investor: {
    value: {
      title: 'Assess technical value',
      description:
        'Add target company repositories to get multi-methodology valuations, IP assessments, and technology risk scoring.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    skills: {
      title: 'Assess team capability',
      description:
        'Add target company repos to analyze their engineering talent -- skill depth, coverage gaps, and key-person risk.',
      actionLabel: 'Import Repos',
      secondaryLabel: 'Run Analysis',
    },
    discovery: {
      title: 'Discover portfolio patterns',
      description:
        'Import repositories across your portfolio companies to spot cross-cutting technology trends and synergy opportunities.',
      actionLabel: 'Import Repos',
      secondaryLabel: 'Run Analysis',
    },
    graph: {
      title: 'Map portfolio dependencies',
      description:
        'Import company repositories to visualize shared technology stacks, common dependencies, and integration potential.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    innovation: {
      title: 'Benchmark innovation maturity',
      description:
        'Analyze target repositories to compare technology adoption patterns against industry benchmarks and portfolio peers.',
      actionLabel: 'Run Analysis',
      secondaryLabel: 'Import Repos',
    },
    assessment: {
      title: 'Run technical due diligence',
      description:
        'Import target repositories to generate automated code quality scores, scalability assessments, and risk matrices.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    classification: {
      title: 'Classify technology assets',
      description:
        'Analyze repositories to categorize a company\'s technical assets by strategic value and defensibility.',
      actionLabel: 'Import Repos',
      secondaryLabel: 'Run Analysis',
    },
    genealogy: {
      title: 'Trace architecture decisions',
      description:
        'Import repositories to understand how the target company\'s technical choices evolved and what that signals.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    export: {
      title: 'Export due diligence reports',
      description:
        'Analyze repositories first, then export detailed technical DD packages for your investment committee.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
  },

  architect: {
    value: {
      title: 'Evaluate integration value',
      description:
        'Import the target system\'s repositories to get architecture compatibility scores, integration complexity estimates, and build-vs-buy analysis.',
      actionLabel: 'Import Target Repos',
      secondaryLabel: null,
    },
    discovery: {
      title: 'Discover architecture patterns',
      description:
        'Analyze target repositories to identify design patterns, service boundaries, and architectural anti-patterns.',
      actionLabel: 'Import Repos',
      secondaryLabel: 'Run Analysis',
    },
    import: {
      title: 'Import the target system',
      description:
        'Connect the repositories you are evaluating to get technical fit scores, dependency analysis, and migration complexity estimates.',
      actionLabel: 'Connect GitHub',
      secondaryLabel: null,
    },
    skills: {
      title: 'Assess team technical depth',
      description:
        'Import repositories to evaluate the engineering team\'s capability across the technology stack you need.',
      actionLabel: 'Import Repos',
      secondaryLabel: 'Run Analysis',
    },
    graph: {
      title: 'Map service dependencies',
      description:
        'Import repositories to visualize microservice boundaries, API contracts, and integration coupling.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    innovation: {
      title: 'Assess technology currency',
      description:
        'Analyze repositories to determine if the target system uses current frameworks or carries legacy debt.',
      actionLabel: 'Run Analysis',
      secondaryLabel: 'Import Repos',
    },
    assessment: {
      title: 'Assess integration readiness',
      description:
        'Import repositories to evaluate API maturity, documentation coverage, and system maintainability.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    classification: {
      title: 'Classify system components',
      description:
        'Analyze repositories to map components by layer -- core domain, infrastructure, integration adapters, and utilities.',
      actionLabel: 'Import Repos',
      secondaryLabel: 'Run Analysis',
    },
    genealogy: {
      title: 'Understand system evolution',
      description:
        'Import repositories to trace architectural decisions and understand why the system is shaped the way it is.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    export: {
      title: 'Export architecture assessment',
      description:
        'Analyze the target system first, then export integration feasibility reports and architecture diagrams.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
  },

  analyst: {
    value: {
      title: 'Start technology valuation',
      description:
        'Import target repositories to run multi-methodology valuations including DCF, comparable analysis, IP assessment, and risk scoring.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    import: {
      title: 'Import target company repos',
      description:
        'Connect the repositories of the company you are evaluating to begin automated technical asset valuation.',
      actionLabel: 'Connect GitHub',
      secondaryLabel: null,
    },
    skills: {
      title: 'Evaluate team assets',
      description:
        'Analyze repositories to quantify engineering team value, key-person dependency, and skill concentration risk.',
      actionLabel: 'Import Repos',
      secondaryLabel: 'Run Analysis',
    },
    discovery: {
      title: 'Discover valuation signals',
      description:
        'Run analysis to surface hidden value drivers -- reusable IP, unique algorithms, and proprietary data pipelines.',
      actionLabel: 'Import Repos',
      secondaryLabel: 'Run Analysis',
    },
    graph: {
      title: 'Map asset dependencies',
      description:
        'Import repositories to visualize how technical assets interconnect and where value concentrates.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    innovation: {
      title: 'Assess technology currency',
      description:
        'Analyze repositories to determine framework age, upgrade debt, and future maintenance cost projections.',
      actionLabel: 'Run Analysis',
      secondaryLabel: 'Import Repos',
    },
    assessment: {
      title: 'Assess technical quality',
      description:
        'Import repositories to generate code quality metrics, technical debt estimates, and maintenance cost projections.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    classification: {
      title: 'Classify by asset type',
      description:
        'Analyze repositories to categorize technical assets for valuation -- core IP, commodity code, and operational tooling.',
      actionLabel: 'Import Repos',
      secondaryLabel: 'Run Analysis',
    },
    genealogy: {
      title: 'Trace IP provenance',
      description:
        'Import repositories to understand code lineage, open-source dependencies, and IP ownership clarity.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    export: {
      title: 'Export valuation report',
      description:
        'Analyze repositories first, then export detailed valuation packages with methodology, comparables, and risk assessments.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
  },

  product_owner: {
    skills: {
      title: 'Map your team capabilities',
      description:
        'Import your team\'s repositories to get skill coverage maps, identify gaps against your roadmap, and plan hiring or upskilling.',
      actionLabel: 'Import Repos',
      secondaryLabel: 'Run Analysis',
    },
    collections: {
      title: 'Organize by product area',
      description:
        'Group repositories by feature domain or squad to track capability coverage and identify cross-team dependencies.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    import: {
      title: 'Connect your team repos',
      description:
        'Link your organization\'s GitHub to map which skills your team has and where the gaps are for upcoming features.',
      actionLabel: 'Connect GitHub',
      secondaryLabel: null,
    },
    value: {
      title: 'Understand your technical investment',
      description:
        'Analyze repositories to see where engineering effort concentrates and which product areas have the most strategic value.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    discovery: {
      title: 'Discover capability gaps',
      description:
        'Run analysis to find skill gaps, technology blind spots, and opportunities for team development aligned with your roadmap.',
      actionLabel: 'Import Repos',
      secondaryLabel: 'Run Analysis',
    },
    graph: {
      title: 'Visualize team collaboration',
      description:
        'Import repositories to see how squads collaborate, where knowledge silos form, and which projects bridge teams.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    innovation: {
      title: 'Track technology adoption',
      description:
        'Analyze repositories to monitor which new technologies teams are adopting and whether adoption is accelerating.',
      actionLabel: 'Run Analysis',
      secondaryLabel: 'Import Repos',
    },
    assessment: {
      title: 'Assess project health',
      description:
        'Import repositories to evaluate code quality, test coverage, and maintenance burden across product areas.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    classification: {
      title: 'Classify by product domain',
      description:
        'Analyze repositories to map them to product areas, user journeys, and business capabilities.',
      actionLabel: 'Import Repos',
      secondaryLabel: 'Run Analysis',
    },
    genealogy: {
      title: 'Trace feature evolution',
      description:
        'Import repositories to understand how product features evolved and which architectural decisions shaped them.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    export: {
      title: 'Export capability reports',
      description:
        'Analyze repositories first, then export team capability maps and product area health dashboards for stakeholders.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
  },

  developer: {
    import: {
      title: 'Import repos to test the API',
      description:
        'Connect your GitHub repositories so you have data to query through the Portfolistic API endpoints.',
      actionLabel: 'Connect GitHub',
      secondaryLabel: null,
    },
    account: {
      title: 'Set up your developer environment',
      description:
        'Generate an API key, explore the documentation, and configure webhook endpoints for your integration.',
      actionLabel: 'Get API Key',
      secondaryLabel: null,
    },
    value: {
      title: 'Query portfolio intelligence via API',
      description:
        'Import repositories first to have data available for the /valuation and /analysis API endpoints.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    skills: {
      title: 'Access skill data programmatically',
      description:
        'Import and analyze repositories to populate the /skills API with verified skill assessments you can query.',
      actionLabel: 'Import Repos',
      secondaryLabel: 'Run Analysis',
    },
    discovery: {
      title: 'Explore AI insights via API',
      description:
        'Import repositories to generate discovery data you can access through the /analysis/insights endpoint.',
      actionLabel: 'Import Repos',
      secondaryLabel: 'Run Analysis',
    },
    graph: {
      title: 'Query relationship data',
      description:
        'Import repositories to populate project relationship data available through the API.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
    export: {
      title: 'Generate reports via API',
      description:
        'Import and analyze repositories first. Then use the /export API endpoints to generate reports programmatically.',
      actionLabel: 'Import Repos',
      secondaryLabel: null,
    },
  },
};

/**
 * Generic fallback copy per tab (used when persona has no specific entry
 * for a given tab, or when no persona is selected).
 */
const GENERIC_EMPTY_STATES = {
  value: {
    title: 'Value Intelligence',
    description:
      'Import repositories to discover the strategic value of your portfolio.',
    actionLabel: 'Import Repos',
    secondaryLabel: null,
  },
  skills: {
    title: 'Skills Matrix',
    description:
      'Run analysis to map your team\'s skills across repositories.',
    actionLabel: 'Import Repos',
    secondaryLabel: 'Run Analysis',
  },
  discovery: {
    title: 'Discovery Engine',
    description:
      'Import repositories and run analysis to discover patterns and insights.',
    actionLabel: 'Import Repos',
    secondaryLabel: 'Run Analysis',
  },
  collections: {
    title: 'Project Collections',
    description:
      'Import repositories to organize them into strategic collections.',
    actionLabel: 'Import Repos',
    secondaryLabel: null,
  },
  graph: {
    title: 'Collaboration Graph',
    description:
      'Import and analyze repositories to visualize project relationships.',
    actionLabel: 'Import Repos',
    secondaryLabel: null,
  },
  innovation: {
    title: 'Innovation Radar',
    description:
      'Run analysis to track technology adoption across your portfolio.',
    actionLabel: 'Run Analysis',
    secondaryLabel: 'Import Repos',
  },
  assessment: {
    title: 'Project Assessment',
    description:
      'Import and analyze repositories to see project assessments.',
    actionLabel: 'Import Repos',
    secondaryLabel: null,
  },
  classification: {
    title: 'Project Classification',
    description:
      'Run analysis on your repositories to generate classifications.',
    actionLabel: 'Import Repos',
    secondaryLabel: 'Run Analysis',
  },
  genealogy: {
    title: 'Project Genealogy',
    description:
      'Import repositories to trace project evolution and relationships.',
    actionLabel: 'Import Repos',
    secondaryLabel: null,
  },
  export: {
    title: 'Export Center',
    description:
      'Import and analyze repositories first, then export reports and data.',
    actionLabel: 'Import Repos',
    secondaryLabel: null,
  },
};

/**
 * Default icon per tab (used when no icon prop is provided).
 */
const TAB_ICONS = {
  value: Gem,
  skills: Target,
  discovery: Brain,
  collections: FolderTree,
  graph: Network,
  innovation: Radar,
  assessment: ClipboardCheck,
  classification: Tag,
  genealogy: GitBranch,
  export: Download,
  import: Github,
};

/**
 * PersonaEmptyState
 *
 * Renders a persona-aware empty state for dashboard tabs.
 * Selects copy from PERSONA_EMPTY_STATES based on the active persona
 * and the tab being viewed. Falls back to generic copy when no
 * persona-specific entry exists.
 *
 * @param {Object} props
 * @param {string}   props.tabId          - The current tab (e.g. 'value', 'skills')
 * @param {React.ComponentType} [props.icon] - Lucide icon component override
 * @param {Function} [props.onAction]     - Primary CTA callback
 * @param {Function} [props.onSecondary]  - Secondary CTA callback
 * @param {string}   [props.overridePersona] - Override persona for testing
 */
const PersonaEmptyState = ({
  tabId,
  icon,
  onAction,
  onSecondary,
  overridePersona,
}) => {
  const { persona } = usePersona();
  const activePersona = overridePersona || persona;

  // Resolve copy: persona-specific > generic > hardcoded fallback
  const personaCopy =
    activePersona && PERSONA_EMPTY_STATES[activePersona]
      ? PERSONA_EMPTY_STATES[activePersona][tabId]
      : null;

  const genericCopy = GENERIC_EMPTY_STATES[tabId];

  const copy = personaCopy || genericCopy || {
    title: 'No data yet',
    description: 'Import repositories to get started.',
    actionLabel: 'Import Repos',
    secondaryLabel: null,
  };

  const Icon = icon || TAB_ICONS[tabId] || Github;

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="inline-flex p-4 rounded-2xl bg-white/5 border border-white/[0.08] mb-4">
        <Icon className="h-10 w-10 text-gray-500" />
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">{copy.title}</h3>
      <p className="text-gray-400 mb-6 max-w-md leading-relaxed">
        {copy.description}
      </p>
      <div className="flex gap-3">
        {copy.actionLabel && onAction && (
          <button
            onClick={onAction}
            className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors font-medium"
          >
            {copy.actionLabel}
          </button>
        )}
        {copy.secondaryLabel && onSecondary && (
          <button
            onClick={onSecondary}
            className="px-5 py-2.5 bg-white/[0.06] hover:bg-white/[0.1] text-gray-300 border border-white/10 rounded-xl transition-colors font-medium"
          >
            {copy.secondaryLabel}
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * SimplePersonaEmptyState
 *
 * A simpler variant that accepts explicit title, description, and action props.
 * Used by persona surface components (InvestorDashboard, DealRoom, etc.)
 * that have their own domain-specific empty state copy.
 *
 * Follows design system: bg-slate-950, glass containers, amber-400 accent, lucide icons.
 *
 * @param {Object} props
 * @param {React.ComponentType} [props.icon] - Lucide icon component (default: FileSearch)
 * @param {string} props.title - Empty state heading
 * @param {string} props.description - Empty state body text
 * @param {string} [props.actionLabel] - CTA button label
 * @param {Function} [props.onAction] - CTA button callback
 * @param {string} [props.accentClass] - Tailwind text color for icon (default: 'text-amber-400')
 */
export const SimplePersonaEmptyState = ({
  icon: Icon = FileSearch,
  title,
  description,
  actionLabel,
  onAction,
  accentClass = 'text-amber-400',
}) => {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="w-16 h-16 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mb-6">
        <Icon className={`w-8 h-8 ${accentClass}`} />
      </div>
      <h3 className="text-lg font-semibold text-white/90 mb-2">{title}</h3>
      <p className="text-sm text-white/50 max-w-md mb-6">{description}</p>
      {actionLabel && onAction && (
        <button
          onClick={onAction}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-400/10 text-amber-400 hover:bg-amber-400/20 transition-colors text-sm font-medium"
        >
          {actionLabel}
          <ArrowRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default PersonaEmptyState;
